import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";
import { uploadDroppedItems } from "utils/upload/uploadDroppedItems";

import { SHAPEFILE_SOURCE } from "components/project/layers/hooks";
import { useProjectDispatch } from "components/project/projects/context";
import { useUploadProjectShapefileList } from "components/project/shapefiles/hooks";

import { FlexItem, LayerActionsWrapper, NodeTitle } from "../../../../shared";
import { useRightClickContextMenu } from "../../../../shared/hooks/useRightClickContextMenu";
import { ProjectLinkedShapefileFolderActions } from "../../../../shared/shapefile-folder-actions/ProjectLinkedShapefileFolderActions";
import { ProjectShapefileFolderActions } from "../../../../shared/shapefile-folder-actions/ProjectShapefileFolderActions";
import { useStructuredNodeContext } from "../../context/StructuredNodeContext";
import { setCanDrop, setInEditMode } from "../../context/reducer/structuredNodeReducer";

export interface IShapefileFolderNode {
  node;
  canSelectItems?: boolean;
}

export function ShapefileFolderNode({ node, canSelectItems }: IShapefileFolderNode) {
  const projectDispatch = useProjectDispatch();
  const [{ canDrop, editableTitle: title }, nodeDispatch] = useStructuredNodeContext();

  const uploadShapefileList = useUploadProjectShapefileList();
  const newFolderPosition =
    Math.max(...node.children.map((child) => child.order || 0)) + 1;

  const { openContextMenu, closeContextMenu, contextMenuObject, nodeRef } =
    useRightClickContextMenu();

  return (
    <NodeTitle
      ref={nodeRef}
      onContextMenu={openContextMenu}
      data-testid="shapefile-folder-container"
      canDrop={canDrop}
      onDragOver={(e) => {
        e.preventDefault();
        const files = getFilesFromEvent(e);
        setCanDrop(nodeDispatch, files.length > 0);
      }}
      // A dropped folder has to be read before it can be uploaded, and the browser
      // empties the dropped list as soon as this handler returns, so the list is
      // handed over before the first await
      onDrop={async (ev) => {
        ev.preventDefault();
        setCanDrop(nodeDispatch, false);
        await uploadDroppedItems({
          transfer: ev.dataTransfer,
          shapefileNodeId: node.key,
          upload: uploadShapefileList.mutate
        });
      }}
      onDragEnter={(e) => {
        const files = getFilesFromEvent(e);
        setCanDrop(nodeDispatch, files.length > 0);
      }}
      onDragLeave={() => {
        setCanDrop(nodeDispatch, false);
      }}>
      <FlexItem>
        <label data-testid="shapefile-folder-title">{title}</label>
      </FlexItem>

      {node.source === SHAPEFILE_SOURCE.PROJECT ? (
        <LayerActionsWrapper>
          <ProjectShapefileFolderActions
            newFolderPosition={newFolderPosition}
            shapefileNodeId={node.shapefileNodeId}
            folderNode={node}
            setIsRenaming={() => {
              setInEditMode(nodeDispatch, true, (isDraggable) =>
                projectDispatch({ payload: { isTreeDraggable: isDraggable } })
              );
              closeContextMenu();
            }}
            folderName={title}
            contextMenu={contextMenuObject}
            hasSelectItemsAction={canSelectItems}
          />
        </LayerActionsWrapper>
      ) : (
        <LayerActionsWrapper>
          <ProjectLinkedShapefileFolderActions
            isOrg={node.source === SHAPEFILE_SOURCE.ORG}
            isMcDan={node.source === SHAPEFILE_SOURCE.MCDAN}
            shapefileNodeId={node.shapefileNodeId}
            contextMenu={contextMenuObject}
            folderName={title}
            hasSelectItemsAction={canSelectItems}
          />
        </LayerActionsWrapper>
      )}
    </NodeTitle>
  );
}
